/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  TextField,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import DatePickerMonthEnds from "../../common/DatePicker/DatePickerMonthEnds";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import { useApprovalContext } from "../../../context/ApprovalContext";
import { useUserContext } from "../../../context/UserContext";
import { minDate, maxDate } from "../../../utils/utils";
import { VIEWTYPE } from "../../../utils/constants";
import ProgressSteps from "../../common/Steps/ProgressSteps";

const mapBusinessEntityID = (businessEntityID) => ({
  id: businessEntityID,
  name: businessEntityID,
});

const mapViewType = (viewType) => ({
  id: viewType,
  name: viewType,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const getApprovalDotColor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "APPROVED":
      return "#2e7d32";
    case "REJECTED":
      return "#d32f2f";
    case "PENDING":
      return "#f9a825";
    case "NOT STARTED":
      return "#9e9e9e";
    default:
      return "#9e9e9e";
  }
};

const ApprovalDot = ({ status }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: getApprovalDotColor(status),
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

const KRISummarySubmissionModelerFiltersAndReports = () => {
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();
  const [isTouched, setIsTouched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selectedViewType, setSelectedViewType] = useState(null);

  const [submittedFilters, setSubmittedFilters] = useState({
    businessEntityID: null,
    monthEnd: null,
    viewType: null,
  });

  const {
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    selectedMonthEnd,
    setSelectedMonthEnd,
    hrMetricsHistoryLoading,
    hrMetricsHistoryError,
    hrMetricsHistory,
    setHrMetricsHistory,
    fetchHrMetricsHistoryData,
    metricsHistoryUpsertAndSave,
  } = useTreasuryKRISummaryContext();

  const {
    fetchApprovalFlowData,
    fetchApprovalFlowDataForKey,
    approvalFlow,
    approvalFlowByKey,
    approvalFlowLoadingByKey,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const buildRowApprovalParams = (metric) => ({
    region: null,
    businessEntityID: submittedFilters.businessEntityID,
    selectedReportDate: submittedFilters.monthEnd,
    measure: metric?.measure || null,
    dimension: metric?.dimension || null,
    dimension2: metric?.dimension2 || null,
    isGlobal: null,
    isModelerReport: null,
    isTreasuryKRIReport: null,
    isKRISummaryReport: true,
    isSFCKRIGlobalSummaryReport: null,
    isRegionKRIGlobalSummaryReport: null,
    isTFSCKRIGlobalSummaryReport: null,
  });

  const buildApprovalKey = (params) =>
    [
      params.region ?? "null",
      params.businessEntityID ?? "null",
      params.measure ?? "null",
      params.dimension ?? "null",
      params.dimension2 ?? "null",
      params.isGlobal ?? "null",
      params.isModelerReport ?? "null",
      params.isTreasuryKRIReport ?? "null",
      params.isKRISummaryReport ?? "null",
      params.isSFCKRIGlobalSummaryReport ?? "null",
      params.isRegionKRIGlobalSummaryReport ?? "null",
      params.isTFSCKRIGlobalSummaryReport ?? "null",
    ].join("|");

  const { firstName, lastName, regions, email } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap(
          (region) => region?.sfcs?.map((sfc) => sfc?.businessEntityID) || [],
        )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)) || []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = [...(allsfcs || []).map(mapBusinessEntityID)];

  const mappedViewTypes = [VIEWTYPE.LOCAL, VIEWTYPE.GLOBAL].map(mapViewType);

  const groupByDimension = (data) =>
    (data || []).reduce((acc, metric) => {
      const key = metric.dimension2
        ? `${metric.dimension}_${metric.dimension2}`
        : metric.dimension;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(metric);
      return acc;
    }, {});

  const getAllReportDates = (data) => {
    const dates = new Set();

    (data || []).forEach((metric) =>
      (metric.entities || []).forEach((entity) =>
        (entity.history || []).forEach((h) => dates.add(h.reportDate)),
      ),
    );

    return Array.from(dates).sort();
  };

  const groupedByDimension = groupByDimension(hrMetricsHistory);
  const reportDates = getAllReportDates(hrMetricsHistory);

  const CATEGORY_WIDTH = 150;
  const MEASURE_WIDTH = 200;
  const LIMIT_WIDTH = 200;

  const stickyCellBase = {
    position: "sticky",
    backgroundColor: "#FAFAFA",
    padding: "6px 12px",
    boxSizing: "border-box",
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setIsTouched(true);
    }
    setIsOpen(open);
  };

  //Measure is Summary for this page
  const APPROVAL_FLOW_PARAMS = {
    region: null,
    businessEntityID: submittedFilters.businessEntityID,
    selectedReportDate: submittedFilters.monthEnd,
    isGlobal: false,
    isModelerReport: false,
    isTreasuryKRIReport: false,
    isKRISummaryReport: true,
    isSFCKRIGlobalSummaryReport: false,
    isRegionKRIGlobalSummaryReport: false,
    isTFSCKRIGlobalSummaryReport: false,
    measure: null,
    dimension: null,
    dimension2: null,
  };

  const handleSubmit = async () => {
    if (!selectedBusinessEntityID || !selectedMonthEnd || !selectedViewType) {
      setHrMetricsHistory([]);
      setApprovalFlow([]);
      return;
    }

    setSubmittedFilters({
      businessEntityID: selectedBusinessEntityID,
      monthEnd: selectedMonthEnd,
      viewType: selectedViewType,
    });

    await fetchHrMetricsHistoryData({
      isGlobal: selectedViewType === VIEWTYPE.GLOBAL,
      isKRISummary: true,
    });

    await fetchApprovalFlowData({
      region: null,
      businessEntityID: selectedBusinessEntityID,
      selectedReportDate: selectedMonthEnd,
      isGlobal: false,
      isModelerReport: false,
      isTreasuryKRIReport: false,
      isKRISummaryReport: true,
      isSFCKRIGlobalSummaryReport: false,
      isRegionKRIGlobalSummaryReport: false,
      isTFSCKRIGlobalSummaryReport: false,
    });
  };

  const handleSendApproverEmailNotify = () => {
    if (selectedBusinessEntityID && selectedMonthEnd) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => {
        return fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
      })
      .then(() => {
        return sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        });
      });
  };

  const isPercentageValue = (value) =>
    value !== null && value !== undefined && String(value).includes("%");

  const isNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const cleaned = String(value).replace("%", "").trim();
    return cleaned !== "" && !Number.isNaN(Number(cleaned));
  };

  const getMetricOptions = (metric) => {
    if (!metric?.options) return [];
    return metric.options.split("|").map((opt) => opt.trim());
  };

  const hasDropdownOptions = (metric) => getMetricOptions(metric).length > 0;

  const getEditType = (cell) => {
    if (!cell) return "text";
    if (hasDropdownOptions(cell.metric)) return "select";
    if (cell.isPercentage || isNumericValue(cell.currentValue)) return "number";
    return "text";
  };

  const handleOpenEdit = ({
    metric,
    entity,
    reportDate,
    field,
    currentValue,
  }) => {
    const isPercentage = isPercentageValue(currentValue);

    setEditingCell({
      metric,
      entity,
      reportDate,
      field,
      currentValue,
      isPercentage,
    });

    setEditValue(
      isPercentage
        ? String(currentValue ?? "").replace("%", "")
        : (currentValue ?? ""),
    );
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingCell) return;

    let finalValue = editValue;

    if (editingCell.isPercentage && editValue !== "") {
      finalValue = `${editValue}%`;
    }

    const payload = {
      dimension: editingCell.metric.dimension,
      dimension2: editingCell.metric.dimension2,
      measure: editingCell.metric.measure,
      businessEntityID: selectedBusinessEntityID,
      value: finalValue,
      isGlobal: selectedViewType === VIEWTYPE.GLOBAL,
      monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
      field: editingCell.field, // unused because limit is non editable
      limit: editingCell.entity?.limit, // unused because limit is non editable
      // reportDate: editingCell.reportDate, // not required
    };

    try {
      await metricsHistoryUpsertAndSave([payload]);

      await fetchHrMetricsHistoryData({
        isGlobal: selectedViewType === VIEWTYPE.GLOBAL,
        isKRISummary: true,
      });

      setEditOpen(false);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleRowStepApproval = async (
    rowParams,
    { currentApprover, type, comment, reportDate },
  ) => {
    await saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    });

    await fetchApprovalFlowDataForKey(rowParams);

    await sendApproverEmailNotifyForSelectedReport({
      ...rowParams,
      selectedReportDate: selectedMonthEnd,
    });
  };

  // Row level approval is not required so making it falsy
  useEffect(() => {
    if (false) {
      if (!submittedFilters.businessEntityID || !submittedFilters.monthEnd)
        return;
      if (!hrMetricsHistory?.length) return;

      const flowPromises = Object.values(groupByDimension(hrMetricsHistory))
        .flat()
        .map((metric) =>
          fetchApprovalFlowDataForKey(buildRowApprovalParams(metric)),
        );
      Promise.allSettled(flowPromises);
    }
  }, [
    hrMetricsHistory,
    submittedFilters.businessEntityID,
    submittedFilters.monthEnd,
  ]);

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below report shows the KRI Summary Submission for the selected
        filters.
      </Typography>
      <>
        {!hrMetricsHistoryError &&
        hrMetricsHistory &&
        hrMetricsHistory?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "10px",
              }}
            >
              {approvalFlow?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendApproverEmailNotify}
                  sx={{
                    fontSize: "0.8rem",
                    boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                    height: "38px",
                    mt: "-10px",
                  }}
                  disabled={
                    selectedUserDetailLoading ||
                    hrMetricsHistoryLoading ||
                    !selectedBusinessEntityID ||
                    !selectedMonthEnd ||
                    sendEmailNotifyLoading
                  }
                >
                  Send Reminder Notification
                </Button>
              )}
            </Box>
            <ProgressSteps
              stepsData={approvalFlow}
              isGlobal={false}
              isModelerReport={false}
              isTreasuryKRIReport={false}
              isKRISummaryReport={true}
              isSFCKRIGlobalSummaryReport={false}
              isRegionKRIGlobalSummaryReport={false}
              isTFSCKRIGlobalSummaryReport={false}
              reportDate={submittedFilters.monthEnd}
              loggedInEmail={email}
              handleStepApproval={handleStepApproval}
            />
          </>
        ) : (
          <Typography
            sx={{
              color: "#6a6a6a",
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            Please generate the report to view the Approval Progress
          </Typography>
        )}
      </>

      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"view-type"}>View Type</InputLabel>
          <Autocomplete
            options={mappedViewTypes}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedViewTypes.find((item) => item.id === selectedViewType) ||
              null
            }
            onChange={(_, newValue) => {
              setSelectedViewType(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={false}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select View Type"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date"}>Month End & Year</InputLabel>
          {selectedUserDetailLoading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={185} height={40} />
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={0}>
                <DatePickerMonthEnds
                  minDate={minDate}
                  maxDate={maxDate}
                  value={selectedMonthEnd}
                  onChange={(newValue) => {
                    setSelectedMonthEnd(newValue?.endOf("month"));
                  }}
                  onOpenChange={handleOpenChange}
                  isOpen={isOpen}
                />
              </Box>
              <Box>
                {selectedMonthEnd === null && isTouched && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      fontStyle: "italic",
                    }}
                  >
                    Month End & Year is required
                  </span>
                )}
              </Box>
            </>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {selectedUserDetailLoading ? (
            <Box display="flex" alignItems="center" sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" width={130} height={40} />
            </Box>
          ) : (
            <TextField
              id="user"
              label="User"
              value={`${firstName} ${lastName}`}
              disabled
              fullWidth
              size="small"
              sx={{ mt: 3 }}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSubmit}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrMetricsHistoryLoading ||
              !selectedBusinessEntityID ||
              !selectedViewType ||
              !selectedMonthEnd
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {hrMetricsHistoryLoading ? (
            <>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                animation="pulse"
                style={{ marginBottom: 8 }}
              />
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={14}
                  animation="pulse"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </>
          ) : !hrMetricsHistoryLoading && hrMetricsHistoryError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrMetricsHistoryLoading && !hrMetricsHistory) ||
            (!hrMetricsHistoryLoading && !hrMetricsHistory?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 600,
                maxWidth: "100%",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  borderRadius: "3px",
                  overflow: "auto",
                  boxShadow: "1px 1px 2px",
                  minWidth: 1200,
                }}
              >
                <TableHead sx={{ backgroundColor: "#eeeeee" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: 0,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: CATEGORY_WIDTH,
                        minWidth: CATEGORY_WIDTH,
                        maxWidth: CATEGORY_WIDTH,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Category
                    </TableCell>

                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: CATEGORY_WIDTH,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: MEASURE_WIDTH,
                        minWidth: MEASURE_WIDTH,
                        maxWidth: MEASURE_WIDTH,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Measure
                    </TableCell>

                    <TableCell
                      sx={{
                        ...stickyCellBase,
                        left: CATEGORY_WIDTH + MEASURE_WIDTH,
                        zIndex: 3,
                        fontWeight: "bold",
                        width: LIMIT_WIDTH,
                        minWidth: LIMIT_WIDTH,
                        maxWidth: LIMIT_WIDTH,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Limit
                    </TableCell>

                    {reportDates.map((date) => (
                      <TableCell
                        key={date}
                        sx={{
                          minWidth: 140,
                          padding: "6px 12px",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{date.split("T")[0]}</span>
                        </Box>
                      </TableCell>
                    ))}

                    {/* <TableCell
                      sx={{
                        minWidth: 340,
                        padding: "6px 12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Approval Action
                    </TableCell> */}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.entries(groupedByDimension).map(
                    ([dimension, metrics]) =>
                      metrics.map((metric, index) => {
                        const entity = metric.entities?.[0];

                        const rowApprovalParams =
                          buildRowApprovalParams(metric);
                        const rowApprovalKey =
                          buildApprovalKey(rowApprovalParams);
                        const rowApprovalFlow =
                          approvalFlowByKey[rowApprovalKey] || [];
                        const rowApprovalLoading =
                          approvalFlowLoadingByKey[rowApprovalKey];

                        return (
                          <TableRow key={metric.metricId}>
                            {index === 0 && (
                              <TableCell
                                rowSpan={metrics.length}
                                sx={{
                                  ...stickyCellBase,
                                  left: 0,
                                  zIndex: 2,
                                  width: CATEGORY_WIDTH,
                                  minWidth: CATEGORY_WIDTH,
                                  maxWidth: CATEGORY_WIDTH,
                                }}
                              >
                                {dimension.replace("_", " -> ")}
                              </TableCell>
                            )}

                            <TableCell
                              sx={{
                                ...stickyCellBase,
                                left: CATEGORY_WIDTH,
                                zIndex: 2,
                                width: MEASURE_WIDTH,
                                minWidth: MEASURE_WIDTH,
                                maxWidth: MEASURE_WIDTH,
                              }}
                            >
                              {metric.measure}
                            </TableCell>

                            <TableCell
                              sx={{
                                ...stickyCellBase,
                                left: CATEGORY_WIDTH + MEASURE_WIDTH,
                                zIndex: 2,
                                width: LIMIT_WIDTH,
                                minWidth: LIMIT_WIDTH,
                                maxWidth: LIMIT_WIDTH,
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{entity?.limit ?? "N/A"}</span>
                                {/* // If need to update the limit (enhancement feature to be tested)
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenEdit({
                                      metric,
                                      entity,
                                      reportDate:
                                        selectedMonthEnd?.format("YYYY-MM-DD"),
                                      field: "limit",
                                      currentValue: entity?.limit ?? "",
                                    })
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton> */}
                              </Box>
                            </TableCell>

                            {reportDates.map((date) => {
                              const currentHistory = entity?.history?.find(
                                (h) => h.reportDate === date,
                              );
                              const currentValue =
                                currentHistory?.value ?? "N/A";
                              const showPercentage =
                                isPercentageValue(currentValue);
                              const displayValue = showPercentage
                                ? String(currentValue).replace("%", "")
                                : currentValue;

                              return (
                                <TableCell key={date}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <span>
                                      {displayValue}
                                      {showPercentage ? "%" : ""}
                                    </span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleOpenEdit({
                                          metric,
                                          entity,
                                          reportDate: date,
                                          field: "history",
                                          currentValue,
                                        })
                                      }
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <ApprovalDot
                                      status={currentHistory?.approvalStatus}
                                    />

                                    {rowApprovalLoading ? (
                                      <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={120}
                                      />
                                    ) : (
                                      <>
                                        <ProgressSteps
                                          stepsData={rowApprovalFlow}
                                          isGlobal={null}
                                          isModelerReport={null}
                                          isTreasuryKRIReport={null}
                                          isKRISummaryReport={true}
                                          isSFCKRIGlobalSummaryReport={null}
                                          isRegionKRIGlobalSummaryReport={null}
                                          isTFSCKRIGlobalSummaryReport={null}
                                          reportDate={submittedFilters.monthEnd}
                                          loggedInEmail={email}
                                          handleStepApproval={(args) =>
                                            handleRowStepApproval(
                                              rowApprovalParams,
                                              args,
                                            )
                                          }
                                          hideProgressSeries={true}
                                          skipStepFilter={true}
                                        />
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}

                            {/* <TableCell
                              sx={{
                                minWidth: 340,
                                verticalAlign: "top",
                                paddingTop: 1,
                              }}
                            >
                              {rowApprovalLoading ? (
                                <Skeleton
                                  variant="rectangular"
                                  width="100%"
                                  height={120}
                                />
                              ) : (
                                <>
                                  <ProgressSteps
                                    stepsData={rowApprovalFlow}
                                    isGlobal={null}
                                    isModelerReport={null}
                                    isTreasuryKRIReport={null}
                                    isKRISummaryReport={true}
                                    isSFCKRIGlobalSummaryReport={null}
                                    isRegionKRIGlobalSummaryReport={null}
                                    isTFSCKRIGlobalSummaryReport={null}
                                    reportDate={submittedFilters.monthEnd}
                                    loggedInEmail={email}
                                    handleStepApproval={(args) =>
                                      handleRowStepApproval(
                                        rowApprovalParams,
                                        args,
                                      )
                                    }
                                    skipStepFilter={true}
                                  />
                                </>
                              )}
                            </TableCell> */}
                          </TableRow>
                        );
                      }),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Value</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Category:</strong>{" "}
            {editingCell?.metric?.dimension2
              ? `${editingCell.metric.dimension} -> ${editingCell.metric.dimension2}`
              : editingCell?.metric?.dimension}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Measure:</strong> {editingCell?.metric?.measure}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Limit:</strong> {editingCell?.entity?.limit ?? "N/A"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Report Date:</strong>{" "}
            {editingCell?.reportDate || selectedMonthEnd?.format("YYYY-MM-DD")}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Current Value:</strong> {editingCell?.currentValue || "N/A"}
          </Typography>

          {getEditType(editingCell) === "select" ? (
            <FormControl fullWidth size="small">
              <InputLabel id="edit-value-label">Value</InputLabel>
              <Select
                labelId="edit-value-label"
                value={editValue}
                label="Value"
                onChange={(e) => setEditValue(e.target.value)}
              >
                {getMetricOptions(editingCell?.metric).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Value"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              type={getEditType(editingCell) === "number" ? "number" : "text"}
              inputProps={
                getEditType(editingCell) === "number"
                  ? { inputMode: "decimal" }
                  : {}
              }
              InputProps={
                editingCell?.isPercentage
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default KRISummarySubmissionModelerFiltersAndReports;
